import { useState } from 'react';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import type { AuthTenant, DbServer, GlobalSettings, Tenant, TenantInput } from '../../api/setup';
import { SkillToggles } from '../knowledge/SkillToggles';
import { FeatureFlagsEditor, ModelSettingsEditor } from './settings';
import {
  AuthTenantLinks,
  EnabledChip,
  SecretField,
  SwitchRow,
  messageOf,
  useQueryParam,
  useQueryPatch,
  useSetup,
} from './shared';

type Tab = 'general' | 'features' | 'settings' | 'skills';
const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'features', label: 'Features' },
  { value: 'settings', label: 'Settings' },
  { value: 'skills', label: 'Skills' },
];

/** Form state: the tenant plus the two write-only secrets as typed (empty = keep). */
type Form = TenantInput & { servicePassword: string; serviceKey: string };

const empty: Form = {
  id: '',
  name: '',
  environment: 'test',
  enabled: true,
  settings: {},
  features: {},
  servicePassword: '',
  serviceKey: '',
  loginFlowSetting: 'auto',
};

const toForm = ({
  hasServicePassword: _p,
  hasServiceKey: _k,
  loginFlow: _l,
  secretError: _s,
  organizations: _o,
  systemCount: _c,
  createdAt: _ca,
  updatedAt: _ua,
  ...t
}: Tenant): Form => ({
  ...t,
  settings: { ...(t.settings ?? {}) },
  features: { ...(t.features ?? {}) },
  servicePassword: '',
  serviceKey: '',
});

/**
 * A copy keeps the connection, scope, log source, server and overrides. Not the secrets (never readable), not the id
 * (one AUTH tenant per installation) and not the database name: a provisioning run must not land in the source's database.
 */
export const copyOf = (t: Tenant): Form => ({ ...toForm(t), id: '', name: `${t.name} (copy)`, dbName: '' });

/** The PUT/POST body: secrets only when typed. */
export function tenantBody(f: Form): TenantInput {
  const { servicePassword, serviceKey, ...rest } = f;
  return {
    ...rest,
    id: rest.id.trim(),
    name: rest.name.trim(),
    dbServerId: rest.dbServerId || null,
    ...(servicePassword ? { servicePassword } : {}),
    ...(serviceKey ? { serviceKey } : {}),
  };
}

const origin = (u: string) => {
  try {
    return new URL(u).origin;
  } catch {
    return u;
  }
};

function TenantEditor({
  initial,
  stored,
  isNew,
  copiedFrom,
  global,
  dbServers,
  canWrite,
  onSave,
  onClose,
}: {
  initial: Form;
  /** The stored row (secret flags); null when new. */
  stored: Tenant | null;
  isNew: boolean;
  copiedFrom?: string;
  global: GlobalSettings | null;
  dbServers: DbServer[];
  canWrite: boolean;
  onSave: (body: TenantInput) => Promise<void>;
  onClose: () => void;
}) {
  const { target, api } = useSetup();
  const [v, setV] = useState(initial);
  const [tabRaw, setTab] = useQueryParam('tab');
  const tab = (TABS.some((t) => t.value === tabRaw) ? tabRaw : 'general') as Tab;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ro = !canWrite;
  // AUTH knows its tenants and the identity provider (= workserver) of each: a new tenant is picked, not typed.
  const authTenants = useAsync(
    () => (isNew ? api.authTenants().catch(() => [] as AuthTenant[]) : Promise.resolve([] as AuthTenant[])),
    [api, isNew]
  );
  const databases = useAsync(
    () => (v.dbServerId ? api.dbServers.databases(v.dbServerId) : Promise.resolve([])),
    [api, v.dbServerId]
  );
  const set = <K extends keyof Form>(k: K, value: Form[K]) => setV((cur) => ({ ...cur, [k]: value }));
  const pick = (id: string) => {
    const t = (authTenants.data ?? []).find((x) => x.id === id);
    if (!t) return;
    setV((cur) => ({
      ...cur,
      id: t.id,
      name: cur.name || t.customerName || t.name || t.id,
      // AUTH posts the credentials to identityProviderUrl (`https://ws/api/authenticate`): the base URL is its origin.
      baseUrl: cur.baseUrl || (t.identityProviderUrl ? origin(t.identityProviderUrl) : ''),
    }));
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(tenantBody(v));
      onClose();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const authOptions = [...(authTenants.data ?? [])].sort((a, b) =>
    (a.customerName || a.name || a.id).localeCompare(b.customerName || b.name || b.id, undefined, {
      sensitivity: 'base',
    })
  );
  const dbs = databases.data ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      title={
        copiedFrom ? `New tenant (copy of ${copiedFrom})` : isNew ? 'New tenant' : `Tenant ${v.name || v.id}`
      }
      maxWidth={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {ro ? 'Close' : 'Cancel'}
          </Button>
          {!ro && (
            <Button onClick={() => void save()} loading={busy} disabled={!v.id.trim() || !v.name.trim()}>
              Save
            </Button>
          )}
        </>
      }
    >
      <ErrorBox error={error} />
      {copiedFrom && (
        <p className="nyk-notice nyk-notice-warn" data-testid="copy-note">
          Copied from <b>{copiedFrom}</b>. Enter the service password and AUTH service key again (they are
          never readable), give it its own AUTH tenant and workserver, and pick its database: the name is left
          blank so a provisioning run cannot land in the source’s database.
        </p>
      )}
      <Tabs value={tab} onChange={setTab} options={TABS} />
      {tab === 'general' && (
        <div className="nyk-form">
          {isNew && !ro && (authTenants.loading || authOptions.length > 0) && (
            <Select
              label="AUTH tenant"
              fieldClassName="nyk-span2"
              value={authOptions.some((t) => t.id === v.id) ? v.id : ''}
              onChange={pick}
              disabled={authTenants.loading}
              options={[
                { value: '', label: authTenants.loading ? 'Reading AUTH…' : 'Pick the installation…' },
                ...authOptions.map((t) => ({
                  value: t.id,
                  label: [t.customerName || t.name || t.id, t.identityProviderUrl]
                    .filter(Boolean)
                    .join(' — '),
                })),
              ]}
              hint="fills in the id, name and workserver; type the id only when AUTH cannot be reached"
            />
          )}
          <TextField
            label="Id (AUTH tenant id)"
            className="nyk-mono"
            value={v.id}
            onChange={(e) => set('id', e.target.value)}
            disabled={!isNew || ro}
            required
          />
          <TextField
            label="Name"
            value={v.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={ro}
            required
          />
          <p className="nyk-ui-hint nyk-span2">
            A tenant is one NYCE.LOGIC <b>installation</b>: one workserver, one user database. Its systems
            carry the organization, the budget and the statistics.
          </p>
          <Select
            label="Environment"
            value={v.environment ?? 'test'}
            onChange={(environment) => set('environment', environment)}
            options={['prod', 'test', 'demo'].map((x) => ({ value: x, label: x }))}
            disabled={ro}
          />
          <TextField
            label="Workserver base URL"
            className="nyk-url"
            value={v.baseUrl ?? ''}
            onChange={(e) => set('baseUrl', e.target.value)}
            placeholder="https://ws.example.com"
            disabled={ro}
          />
          <TextField
            label="Query endpoint"
            className="nyk-url"
            value={v.apiUrl ?? ''}
            onChange={(e) => set('apiUrl', e.target.value)}
            hint="empty = the workserver's nyceBuddy v3 route"
            disabled={ro}
          />
          <TextField
            label="Service user"
            value={v.serviceUser ?? ''}
            onChange={(e) => set('serviceUser', e.target.value)}
            disabled={ro}
          />
          <SecretField
            label="Service password"
            value={v.servicePassword}
            stored={stored?.hasServicePassword}
            isNew={isNew}
            onChange={(x) => set('servicePassword', x)}
            disabled={ro}
          />
          <SecretField
            label="AUTH service key"
            value={v.serviceKey}
            stored={stored?.hasServiceKey}
            isNew={isNew}
            onChange={(x) => set('serviceKey', x)}
            disabled={ro}
          />
          {stored?.secretError && (
            <p className="nyk-notice nyk-notice-warn nyk-span2">{stored.secretError}</p>
          )}
          <Select
            label="Sign-in flow"
            value={v.loginFlowSetting ?? 'auto'}
            onChange={(f) => set('loginFlowSetting', f)}
            options={[
              { value: 'auto', label: 'auto (exchange when a service key is stored)' },
              { value: 'token', label: 'token — AUTH signs the user in' },
              { value: 'exchange', label: 'exchange — nyceBuddy signs in, AUTH exchanges' },
            ]}
            hint={
              stored?.loginFlow
                ? `resolves to ${stored.loginFlow}; ticket sign-in (add-in panel, launch codes) always exchanges`
                : 'token needs AUTH to reach the workserver; exchange needs the service key'
            }
            disabled={ro}
          />
          <TextField
            label="Client"
            value={v.client ?? ''}
            onChange={(e) => set('client', e.target.value)}
            disabled={ro}
          />
          <TextField
            label="Warehouse"
            value={v.warehouse ?? ''}
            onChange={(e) => set('warehouse', e.target.value)}
            disabled={ro}
          />
          <TextField
            label="Client group"
            value={v.clientGroup ?? ''}
            onChange={(e) => set('clientGroup', e.target.value)}
            disabled={ro}
          />
          <TextField
            label="Log namespace"
            value={v.logNamespace ?? ''}
            onChange={(e) => set('logNamespace', e.target.value)}
            disabled={ro}
          />
          <TextField
            label="Log filter"
            className="nyk-mono"
            fieldClassName="nyk-span2"
            value={v.logFilter ?? ''}
            onChange={(e) => set('logFilter', e.target.value)}
            hint="extra Cloud Logging clause ANDed onto every query_logs search"
            disabled={ro}
          />
          <Select
            label="Database server (provisioning)"
            value={v.dbServerId ?? ''}
            onChange={(id) =>
              setV((cur) => ({
                ...cur,
                dbServerId: id || null,
                dbName: id === cur.dbServerId ? cur.dbName : '',
              }))
            }
            options={[
              { value: '', label: 'None: provision with the downloaded SQL' },
              ...dbServers.map((d) => ({
                value: d.id,
                label: `${d.name || d.id} (${d.host})`,
                group: d.cluster || 'Ungrouped',
              })),
            ]}
            disabled={ro}
          />
          {v.dbServerId && !databases.error ? (
            <Select
              label="NYCE.LOGIC database"
              value={v.dbName ?? ''}
              onChange={(dbName) => set('dbName', dbName)}
              disabled={ro || databases.loading}
              options={[
                { value: '', label: databases.loading ? 'Reading the server…' : 'None' },
                ...dbs
                  .filter((d) => d.nyceLogic !== false)
                  .map((d) => ({ value: d.name, label: d.name, group: 'NYCE.LOGIC' })),
                ...dbs
                  .filter((d) => d.nyceLogic === false)
                  .map((d) => ({ value: d.name, label: d.name, group: 'Other databases' })),
                ...(v.dbName && !dbs.some((d) => d.name === v.dbName)
                  ? [{ value: v.dbName, label: `${v.dbName} (not listed)`, group: 'Other databases' }]
                  : []),
              ]}
            />
          ) : (
            <TextField
              label="NYCE.LOGIC database"
              value={v.dbName ?? ''}
              onChange={(e) => set('dbName', e.target.value.trim())}
              placeholder="nycelogic_acme"
              hint={
                databases.error
                  ? `Could not list databases (${databases.error}); type the name`
                  : 'pick a database server to choose from its databases'
              }
              disabled={ro}
            />
          )}
          <SwitchRow
            label={
              v.enabled
                ? 'Enabled: its systems accept users'
                : 'Disabled: every system of the tenant refuses sign-in'
            }
            checked={v.enabled}
            onChange={(enabled) => set('enabled', enabled)}
            disabled={ro}
          />
        </div>
      )}
      {tab === 'features' && (
        <FeatureFlagsEditor
          value={v.features ?? {}}
          onChange={(features) => set('features', features)}
          inherited={global?.features ?? {}}
          disabled={ro}
        />
      )}
      {tab === 'settings' && (
        <ModelSettingsEditor
          value={v.settings ?? {}}
          onChange={(settings) => set('settings', settings)}
          inherited={global?.settings ?? {}}
          disabled={ro}
        />
      )}
      {tab === 'skills' &&
        (isNew ? (
          <EmptyState title="Save the tenant first." />
        ) : (
          <SkillToggles target={target} scope="tenant" scopeId={v.id} />
        ))}
    </Dialog>
  );
}

/** Tenants = AUTH tenants = NYCE.LOGIC installations: connection, service account, logs and provisioning target. */
export function BuddyTenantsPage() {
  const { target, api } = useSetup();
  const canWrite = useBuddyWrite();
  const tenants = useAsync(() => api.tenants.list(), [api]);
  const global = useAsync(() => api.globalSettings.get().catch(() => null), [api]);
  const dbServers = useAsync(() => api.dbServers.list().catch(() => [] as DbServer[]), [api]);
  const [edit] = useQueryParam('edit');
  const [copyFrom] = useQueryParam('copy');
  const patch = useQueryPatch();
  const toast = useNotice();
  const open = (id: string, copy?: string) => patch({ edit: id, copy: copy ?? null, tab: null });
  const close = () => patch({ edit: null, copy: null, tab: null });

  const list = tenants.data ?? [];
  const isNew = edit === 'new' && canWrite;
  const source = isNew && copyFrom ? list.find((t) => t.id === copyFrom) : undefined;
  const stored = isNew ? null : (list.find((t) => t.id === edit) ?? null);
  const initial = isNew ? (source ? copyOf(source) : empty) : stored ? toForm(stored) : null;

  const save = async (body: TenantInput) => {
    if (isNew) await api.tenants.create(body);
    else await api.tenants.update(body.id, body);
    toast.success(`Saved ${body.name}`);
    await tenants.reload();
  };
  const remove = async (t: Tenant) => {
    try {
      await api.tenants.remove(t.id);
      toast.success(`Deleted ${t.name}`);
      await tenants.reload();
    } catch (e) {
      toast.error(`Could not delete ${t.name}: ${messageOf(e)}`);
    }
  };

  return (
    <Page
      title="Tenants"
      description="One per AUTH tenant: the NYCE.LOGIC installation (workserver, service account, logs, database) its systems register."
      actions={canWrite && <Button onClick={() => open('new')}>New tenant</Button>}
    >
      <ErrorBox error={tenants.error} />
      {toast.element}
      <Loading loading={tenants.loading && !tenants.data} />
      {tenants.data && !list.length && (
        <EmptyState
          card
          title="No tenants yet."
          hint="Create one per AUTH tenant before you register a system."
        />
      )}
      {!!list.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="tenants-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Workserver</th>
                  <th>Organizations</th>
                  <th className="nyk-ui-num">Systems</th>
                  <th>Secrets</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} data-tenant={t.id}>
                    <td className="nyk-card-title">
                      <b>{t.name}</b>
                      <div className="nyk-ui-hint nyk-mono">{t.id}</div>
                      <AuthTenantLinks tenantId={t.id} target={target} />
                    </td>
                    <td className="nyk-url" data-label="Workserver">
                      {t.baseUrl || <span className="nyk-ui-hint">—</span>}
                      {t.environment && (
                        <div>
                          <Chip size="sm">{t.environment}</Chip>
                        </div>
                      )}
                    </td>
                    <td data-label="Organizations">
                      {t.organizations?.length ? (
                        t.organizations.map((o) => (
                          <div key={o.id}>
                            {o.name} <span className="nyk-ui-hint">{o.id}</span>
                          </div>
                        ))
                      ) : (
                        <span className="nyk-ui-hint">—</span>
                      )}
                    </td>
                    <td className="nyk-ui-num" data-label="Systems">
                      {t.systemCount ?? 0}
                    </td>
                    <td data-label="Secrets">
                      <div className="nyk-ui-hint" data-secrets={t.id}>
                        password {t.hasServicePassword ? 'stored' : 'not set'} · key{' '}
                        {t.hasServiceKey ? 'stored' : 'not set'}
                      </div>
                      {t.secretError && <div className="nyk-text-red">{t.secretError}</div>}
                    </td>
                    <td data-label="Status">
                      <EnabledChip enabled={t.enabled} />
                    </td>
                    <td className="nyk-row-actions">
                      <Button size="sm" variant="ghost" onClick={() => open(t.id)}>
                        {canWrite ? 'Edit' : 'View'}
                      </Button>
                      {canWrite && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => open('new', t.id)}>
                            Copy
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="danger"
                            message={`Delete tenant ${t.name} and its systems?`}
                            onConfirm={() => void remove(t)}
                          >
                            Delete
                          </ConfirmButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {initial && (!isNew || !copyFrom || source || tenants.data) && (
        <TenantEditor
          key={`${edit}:${copyFrom}`}
          initial={initial}
          stored={stored}
          isNew={isNew}
          copiedFrom={source?.name}
          global={global.data ?? null}
          dbServers={dbServers.data ?? []}
          canWrite={canWrite}
          onSave={save}
          onClose={close}
        />
      )}
    </Page>
  );
}
